// Taken from https://github.com/marmelab/react-admin/blob/4865cb699c72f15cc32c8808ba1b4848ff1f086f/packages/ra-ui-materialui/src/auth/Login.tsx

import type { LoginMethod } from "@Common/types/better_auth";
import { LoginForm } from "@Src/layout/LoginForm";
import LockIcon from "@mui/icons-material/Lock";
import { Avatar, Card, type SxProps } from "@mui/material";
import {
	type ComponentsOverrides,
	styled,
	type Theme,
	useThemeProps,
} from "@mui/material/styles";
import {
	type HtmlHTMLAttributes,
	type ReactNode,
	useEffect,
	useRef,
} from "react";

/**
 * A standalone login page, to serve as authentication gate to the admin
 *
 * Expects the user to enter a login and a password, which will be checked
 * by the `authProvider.login()` method. Redirects to the root page (/)
 * upon success, otherwise displays an authentication error message.
 *
 * Copy and adapt this component to implement your own login logic
 * (e.g. to authenticate via email or facebook or anything else).
 *
 * @example
 *     import MyLoginPage from './MyLoginPage';
 *     const App = () => (
 *         <Admin loginPage={MyLoginPage} authProvider={authProvider}>
 *             ...
 *        </Admin>
 *     );
 */
export const Login = (inProps: LoginProps & { context: LoginMethod }) => {
	const props = useThemeProps({
		props: inProps,
		name: PREFIX,
	});
	const { backgroundImage, avatarIcon = defaultAvatarIcon, ...rest } = props;
	const { context } = inProps;
	const containerRef = useRef<HTMLDivElement>(null);
	let backgroundImageLoaded = false;

	// Note: The original implementation called useCheckAuth() here to redirect
	// already-authenticated users away from the login page. However, this caused
	// "Failed to get session" 400 errors and unwanted toaster notifications when
	// unauthenticated users navigated to login from public pages.
	//
	// React-admin's routing and authProvider already handle redirecting authenticated
	// users appropriately, so the explicit checkAuth call is unnecessary and causes
	// more harm than good. We've removed it to fix the sign-in page bug (Ticket 29).

	const updateBackgroundImage = () => {
		if (!backgroundImageLoaded && containerRef.current) {
			containerRef.current.style.backgroundImage = `url(${backgroundImage})`;
			backgroundImageLoaded = true;
		}
	};

	// Load background image asynchronously to speed up time to interactive
	const lazyLoadBackgroundImage = () => {
		if (backgroundImage) {
			const img = new Image();
			img.onload = updateBackgroundImage;
			img.src = backgroundImage;
		}
	};

	useEffect(() => {
		if (!backgroundImageLoaded) {
			lazyLoadBackgroundImage();
		}
	});
	return (
		<Root {...rest} ref={containerRef}>
			<Card className={LoginClasses.card}>
				<div className={LoginClasses.avatar}>
					<Avatar className={LoginClasses.icon}>{avatarIcon}</Avatar>
				</div>
				<LoginForm context={context} />
			</Card>
		</Root>
	);
};

const defaultAvatarIcon = <LockIcon />;

export interface LoginProps extends HtmlHTMLAttributes<HTMLDivElement> {
	avatarIcon?: ReactNode;
	backgroundImage?: string;
	children?: ReactNode;
	className?: string;
	sx?: SxProps<Theme>;
}

const PREFIX = "RaLogin";
export const LoginClasses = {
	card: `${PREFIX}-card`,
	avatar: `${PREFIX}-avatar`,
	icon: `${PREFIX}-icon`,
};

const Root = styled("div", {
	name: PREFIX,
	overridesResolver: (_props, styles) => styles["root"],
})(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	minHeight: "100vh",
	height: "1px",
	alignItems: "center",
	justifyContent: "flex-start",
	backgroundRepeat: "no-repeat",
	backgroundSize: "cover",
	backgroundColor: theme.palette.background.default,

	[`& .${LoginClasses.card}`]: {
		minWidth: 300,
		marginTop: "6em",
	},
	[`& .${LoginClasses.avatar}`]: {
		margin: "1em",
		display: "flex",
		justifyContent: "center",
	},
	[`& .${LoginClasses.icon}`]: {
		// @ts-expect-error comes from RA source code
		backgroundColor: (theme.vars || theme).palette.secondary[500],
	},
}));

declare module "@mui/material/styles" {
	interface ComponentNameToClassKey {
		RaLogin: "root" | "card" | "avatar" | "icon";
	}

	interface ComponentsPropsList {
		RaLogin: Partial<LoginProps>;
	}

	interface Components {
		RaLogin?: {
			defaultProps?: ComponentsPropsList["RaLogin"];
			styleOverrides?: ComponentsOverrides<
				Omit<Theme, "components">
			>["RaLogin"];
		};
	}
}
